import { WolfLink } from "@/pages/WolfPage";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Flame } from "lucide-react";
import { api } from "@/lib/api";
import HolderBoard from "@/components/HolderBoard";

const TRAITS = [["fur", "Fur"], ["outfit", "Outfit"], ["headwear", "Headwear"], ["extra", "Extra"], ["background", "Background"]];

function WolfCard({ w }) {
  return (
    <article data-testid={`gallery-card-${w.no}`} className="group rounded-2xl overflow-hidden border border-slate-800 bg-[#081820]/80 hover:border-amber-400/60 hover:-translate-y-1 transition-[transform,border-color] duration-300">
      <div className="relative aspect-square overflow-hidden">
        <img src={`${process.env.REACT_APP_BACKEND_URL}${w.image}`} alt={w.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/75 text-[11px] font-mono-game text-amber-200" data-testid={`gallery-no-${w.no}`}>#{w.no.toLocaleString()}</span>
        {w.burned && <span title="Minted straight into a wallet nobody can open, so it's out of circulation forever" className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/90 text-[11px] font-semibold text-white shadow-lg shadow-red-900/50" data-testid={`gallery-burned-${w.no}`}><Flame className="w-3 h-3" /> Burned</span>}
      </div>
      <div className="p-4 space-y-2">
        <p className="font-display text-lg text-white truncate">{w.name}</p>
        <p className="text-xs text-slate-400">Owned by <span className="text-amber-200 font-semibold" data-testid={`gallery-owner-${w.no}`}>{w.burned || w.owner === "A collector" ? w.owner : <WolfLink name={w.owner} />}</span></p>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          {(w.traits.theme ? [["theme", "Theme"], ["background", "Background"]] : TRAITS).map(([k, label]) => (
            <div key={k} className={`min-w-0 ${k === "theme" ? "col-span-2" : ""}`}><dt className="text-slate-500">{label}</dt><dd className="text-slate-200 truncate" title={w.traits[k]}>{w.traits[k]}</dd></div>
          ))}
        </dl>
        {w.explorer && <a href={w.explorer} target="_blank" rel="noreferrer" data-testid={`gallery-view-${w.no}`} className="text-[11px] text-cyan-300 hover:underline inline-flex items-center gap-1">View on Solana <ExternalLink className="w-3 h-3" /></a>}
      </div>
    </article>
  );
}

function Series({ path, tid, kicker, title, unit, units = `${unit}s`, countText, className }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(path).then(({ data }) => setItems(data.items)).catch(() => {}); }, [path]);
  if (!items.length) return null;
  return (
    <section data-testid={tid} className={`mb-14 rounded-3xl border p-6 sm:p-8 ${className}`}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] font-mono-game text-slate-400 mb-2">{kicker}</p>
          <h2 className="font-display text-3xl text-white">{title}</h2>
        </div>
        <p data-testid={`${tid}-count`} className="font-mono-game text-xs text-amber-200">{items.length} {items.length === 1 ? unit : units} {countText}</p>
      </div>
      <div className="flex gap-5 overflow-x-auto pb-2 snap-x">
        {items.map((w) => <div key={w.id} className="w-64 shrink-0 snap-start"><WolfCard w={w} /></div>)}
      </div>
    </section>
  );
}

export default function Gallery() {
  const [d, setD] = useState(null);
  const [items, setItems] = useState([]);
  const load = (skip) => api.get("/gallery/wolves", { params: { skip, limit: 48 } }).then(({ data }) => { setD(data); setItems((x) => (skip ? [...x, ...data.items] : data.items)); }).catch(() => setD({ items: [], total: 0, cap: 10000 }));
  useEffect(() => { load(0); }, []);
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="gallery-page">
      <p className="text-xs uppercase tracking-[0.3em] font-mono-game text-amber-400/90 mb-3">Solana collection</p>
      <h1 className="font-display text-4xl sm:text-5xl text-white mb-3">Wolfpack Games 1/1 Wolves</h1>
      <p className="text-slate-400 mb-3 max-w-2xl">Every one-of-a-kind wolf that has been claimed as an NFT. Only 10,000 will ever exist, each one wearable in the game.</p>
      {d && <p className="font-mono-game text-xs text-amber-200 mb-10" data-testid="gallery-count">{d.total.toLocaleString()} claimed of {d.cap.toLocaleString()}</p>}
      <HolderBoard limit={5} compact />
      <Series path="/gallery/series/agents" tid="gallery-agent-series" kicker="Classified · Series" title="The Agent Series" unit="agent" countText="in the field"
        className="border-slate-700/70 bg-[linear-gradient(135deg,#05080c,#0b1620)]" />
      <Series path="/gallery/series/robot-city" tid="gallery-robot-city-series" kicker="Collab · Robot City" title="Robot City Series" unit="wolf" units="wolves" countText="minted"
        className="border-fuchsia-500/40 bg-[linear-gradient(135deg,#0a0520,#071a2e)] shadow-[0_0_40px_-12px_rgba(217,70,239,0.45)]" />
      {!d ? <p className="text-slate-500">Gathering the wolves…</p> : !items.length ? (
        <div data-testid="gallery-empty" className="text-center py-16 text-slate-500">
          No wolves have been claimed yet. <Link to="/store" className="text-amber-300 hover:underline">Get a 1/1 wolf</Link> and be the first.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="gallery-grid">{items.map((w) => <WolfCard key={w.id} w={w} />)}</div>
          {items.length < d.total && (
            <div className="text-center mt-10"><button data-testid="gallery-load-more" onClick={() => load(items.length)} className="px-6 py-2.5 rounded-full border border-slate-600 text-sm hover:border-amber-300">Show more</button></div>
          )}
        </>
      )}
    </main>
  );
}
